<template>
  <div class="page-header">
    <!-- 左侧返回区域 -->
    <div
      v-if="showBack"
      class="back-btn"
      :class="{ 'only-back': !title && !$slots.title && !$slots.right }"
      @click="handleBackClick"
    >
      <!-- 支持两种返回图标：图片或 CSS 图形 -->
      <img
        v-if="useImageIcon"
        class="back-icon"
        src="@/static/icon/backw.png"
        mode="aspectFit"
      />
      <div v-else class="back-icon css-arrow" />

      <!-- 返回文字 -->
      <span v-if="backText" class="back-text">{{ backText }}</span>
    </div>

    <!-- 中间标题区域：优先插槽，其次 props.title -->
    <div v-if="$slots.title || title" class="title-container">
      <slot name="title">
        <span class="title-text">{{ title }}</span>
      </slot>
    </div>

    <!-- 右侧插槽 / 占位块 -->
    <div v-if="$slots.right" class="right-slot">
      <slot name="right" />
    </div>
    <div v-else-if="showBack && (title || $slots.title)" class="right-placeholder" />
  </div>
</template>

<script>
export default {
  name: 'NavHeader',
  props: {
    // 标题文字
    title: {
      type: String,
      default: ''
    },
    // 是否显示返回按钮
    showBack: {
      type: Boolean,
      default: true
    },
    // 返回按钮文字（传空字符串则不显示文字）
    backText: {
      type: String,
      default() {
        return this.$t("common.back")
      }
    },
    // 是否使用图片图标（true 使用图片，false 使用 CSS 绘制的箭头）
    useImageIcon: {
      type: Boolean,
      default: true
    },
    // 自定义返回前的钩子函数，若返回 false 或 reject 则阻止返回
    beforeBack: {
      type: Function,
      default: null
    },
    // 完全自定义返回逻辑（若提供，则代替默认返回行为）
    customBack: {
      type: Function,
      default: null
    }
  },
  methods: {
    async handleBackClick() {
      // 1. 如果有自定义返回函数，直接调用
      if (this.customBack) {
        this.customBack();
        return;
      }

      // 2. 执行前置钩子（支持异步和返回值判断）
      if (this.beforeBack) {
        try {
          const canGoBack = await this.beforeBack();
          if (canGoBack === false) return;
        } catch (e) {
          console.error('beforeBack hook error:', e);
          return;
        }
      }

      // 3. 默认返回逻辑：Vue Router 后退
      //    注意：需要确保组件在路由上下文中使用，且 history 模式有上一页
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        // 无历史记录时的降级处理，可自定义跳转首页等
        console.warn('No previous page in history');
        // 可选：跳转到首页
        // this.$router.push('/');
      }
    }
  }
};
</script>
<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 49px;
  padding: 0 16px;
  background: rgba(245, 245, 247, 0.9);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 20;
  box-sizing: border-box;
  color: #1d1d1f;
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
}

/* 返回区域 */
.back-btn {
  display: inline-flex;
  align-items: center;
  height: 100%;
  min-width: 84px;
  z-index: 2;
  cursor: pointer;
  color: #007aff;
}

.back-btn.only-back {
  min-width: auto;
}

/* 图标 */
.back-icon {
  width: 18px;
  height: 18px;
  margin-right: 6px;
  flex-shrink: 0;
  filter: brightness(0) saturate(100%) invert(36%) sepia(88%) saturate(1907%) hue-rotate(196deg) brightness(102%) contrast(101%);
}

/* CSS 箭头 */
.back-icon.css-arrow {
  width: 10px;
  height: 10px;
  border-left: 2px solid #007aff;
  border-bottom: 2px solid #007aff;
  transform: rotate(45deg);
  margin-right: 8px;
  filter: none;
}

/* 返回文字 */
.back-text {
  font-size: 14px;
  font-weight: 500;
  color: #007aff;
  line-height: 1;
}

/* 标题 */
.title-container {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100% - 200px);
  text-align: center;
  z-index: 1;
  pointer-events: none;
}

.title-text {
  font-size: 17px;
  font-weight: 600;
  color: #1d1d1f;
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 右侧 */
.right-slot,
.right-placeholder {
  min-width: 88px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  z-index: 2;
}

/* 右侧文字/按钮统一白色 */
.page-header :deep(.right-btn),
.page-header :deep(.right-text) {
  font-size: 14px;
  color: #007aff;
}

/* 点击态（更有质感） */
.back-btn:active,
.page-header :deep(.right-btn:active),
.page-header :deep(.right-text:active) {
  opacity: 0.7;
}
</style>
